import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import type {
  AnalyticsData, ReportRow, ProjectHistory, ProjectFinancials,
  ProjectStats, JsPDFWithAutoTable, FilterMode, MonthlyHistoryPoint
} from '../pages/reporting/types';
import { MONTHS_FR } from '../pages/reporting/types';

interface UseReportExportsParams {
  analytics: AnalyticsData;
  payrollData: ReportRow[];
  projectHistory: ProjectHistory | null;
  projectFinancials: ProjectFinancials | null;
  projectStats: ProjectStats[];
  projectList: { id: string; code: string; name: string }[];
  employeeList: { id: string; name: string }[];
  selectedProject: string;
  selectedEmployee: string;
  periodLabel: string;
  dbPeriodStr: string;
  startDate: string;
  endDate: string;
  filterMode: FilterMode;
  selectedYear: number;
  selectedMonth: number;
  selectedWeek: number;
  historyViewMode: string;
  showCumulative: boolean;
  aggregateByYear: (data: MonthlyHistoryPoint[]) => MonthlyHistoryPoint[];
}

export function useReportExports({
  analytics,
  payrollData,
  projectHistory,
  projectList,
  employeeList,
  selectedProject,
  selectedEmployee,
  periodLabel,
  dbPeriodStr,
  startDate,
  endDate,
  filterMode,
  selectedYear,
  selectedMonth,
  selectedWeek,
}: UseReportExportsParams) {

  // --- EXPORT PDF ANALYTIQUE ---
  const handleExportPDFAnalytics = useCallback(() => {
    const doc = new jsPDF();
    const totalGlobal = analytics.global.etude + analytics.global.suivi + analytics.global.mise_en_service + analytics.global.chantier;

    // Récupérer les infos de filtre
    const selectedProjectInfo = selectedProject !== 'all' ? projectList.find(p => p.id === selectedProject) : null;
    const selectedEmployeeInfo = selectedEmployee !== 'all' ? employeeList.find(e => e.id === selectedEmployee) : null;

    // Titre principal (avec nom du chantier si filtré)
    doc.setFontSize(18);
    doc.setTextColor(0, 133, 66);
    if (selectedProjectInfo) {
      doc.text(`RAPPORT - ${selectedProjectInfo.name}`, 14, 20);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Code: ${selectedProjectInfo.code}`, 14, 28);
    } else {
      doc.text("RAPPORT D'ANALYSE DES HEURES", 14, 20);
    }

    // Sous-titre avec période et employé filtré
    let yPos = selectedProjectInfo ? 36 : 30;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Période : ${periodLabel}`, 14, yPos);

    if (selectedEmployeeInfo) {
      yPos += 6;
      doc.text(`Employé : ${selectedEmployeeInfo.name}`, 14, yPos);
    }

    // Affichage du nombre de GD s'il y en a (basé sur les entrées, pas la catégorie du projet)
    if (analytics.gdDaysCount > 0) {
      yPos += 6;
      doc.setTextColor(220, 38, 38); // Rouge pour GD
      doc.setFont('helvetica', 'bold');
      doc.text(`Grand Déplacement : ${analytics.gdDaysCount} jour${analytics.gdDaysCount > 1 ? 's' : ''}`, 14, yPos);
      doc.setFont('helvetica', 'normal');
    }

    yPos += 15;
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Répartition Globale", 14, yPos);

    autoTable(doc, {
      startY: yPos + 5,
      head: [['Type d\'activité', 'Heures', '%']],
      body: [
        ['Bureau d\'Étude', analytics.global.etude.toFixed(2) + ' h', totalGlobal > 0 ? ((analytics.global.etude / totalGlobal) * 100).toFixed(1) + '%' : '0%'],
        ['Suivi de Chantier', analytics.global.suivi.toFixed(2) + ' h', totalGlobal > 0 ? ((analytics.global.suivi / totalGlobal) * 100).toFixed(1) + '%' : '0%'],
        ['Mise en Service', analytics.global.mise_en_service.toFixed(2) + ' h', totalGlobal > 0 ? ((analytics.global.mise_en_service / totalGlobal) * 100).toFixed(1) + '%' : '0%'],
        ['Production Chantier', analytics.global.chantier.toFixed(2) + ' h', totalGlobal > 0 ? ((analytics.global.chantier / totalGlobal) * 100).toFixed(1) + '%' : '0%'],
        ['TOTAL', totalGlobal.toFixed(2) + ' h', '100%']
      ],
      theme: 'striped',
      headStyles: { fillColor: [0, 133, 66] },
      footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold' }
    });

    const finalY = (doc as unknown as JsPDFWithAutoTable).lastAutoTable.finalY + 15;
    doc.text("Détail par Chantier", 14, finalY);

    const projectRows = Object.values(analytics.byProject)
      .sort((a, b) => b.total - a.total)
      .map(p => [p.code, p.name, p.etude.toFixed(2), p.suivi.toFixed(2), p.mise_en_service.toFixed(2), p.chantier.toFixed(2), p.total.toFixed(2)]);

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Code', 'Nom du Chantier', 'Étude', 'Suivi', 'MES', 'Chantier', 'Total']],
      body: projectRows,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [71, 85, 105] },
      columnStyles: { 0: { fontStyle: 'bold' }, 6: { fontStyle: 'bold', fillColor: [245, 245, 245] } }
    });

    // Nom du fichier avec filtres appliqués
    let fileName = `analyse_heures_${dbPeriodStr}`;
    if (selectedProjectInfo) fileName += `_${selectedProjectInfo.code}`;
    if (selectedEmployeeInfo) fileName += `_${selectedEmployeeInfo.name.replace(/\s+/g, '_')}`;
    doc.save(`${fileName}.pdf`);
  }, [analytics, selectedProject, selectedEmployee, projectList, employeeList, periodLabel, dbPeriodStr]);

  // --- EXPORT EXCEL ---
  const downloadXLSX = useCallback(async () => {
    // Charger les congés validés pour la période
    const { data: leavesData } = await supabase
      .from('leaves')
      .select('employee_id, type, start_date, end_date')
      .eq('status', 'validated')
      .lte('start_date', endDate)
      .gte('end_date', startDate);

    // Calculer les jours de congés par employé et par type
    const countDays = (s: string, e: string): number => {
      const start = new Date(s);
      const end = new Date(e);
      const from = new Date(Math.max(start.getTime(), new Date(startDate).getTime()));
      const to = new Date(Math.min(end.getTime(), new Date(endDate).getTime()));
      let days = 0;
      const d = new Date(from);
      while (d <= to) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) days++;
        d.setDate(d.getDate() + 1);
      }
      return days;
    };

    const leavesByEmployee: Record<string, Record<string, number>> = {};
    for (const leave of leavesData || []) {
      if (!leavesByEmployee[leave.employee_id]) {
        leavesByEmployee[leave.employee_id] = { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
      }
      leavesByEmployee[leave.employee_id][leave.type] += countDays(leave.start_date, leave.end_date);
    }

    const dataForExcel = payrollData.map(row => {
      const leaves = leavesByEmployee[row.employeeId] || { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
      return {
        "Employé": row.employeeName,
        "Rôle": row.role,
        "Total Heures": Number(row.totalHours.toFixed(2)),
        "HS 25%": Number(row.overtime25.toFixed(2)),
        "HS 50%": Number(row.overtime50.toFixed(2)),
        "H. Dimanche (100%)": Number(row.sundayHours.toFixed(2)),
        "Total H. Supp": Number(row.overtime.toFixed(2)),
        "Paniers Repas": row.totalMeals,
        "Total Transport": row.totalTransport,
        "Total Trajet": row.totalTrajet,
        "Zone 1A": row.zones["1A"] || 0,
        "Zone 1B": row.zones["1B"] || 0,
        "Zone 2": row.zones["2"] || 0,
        "Zone 3": row.zones["3"] || 0,
        "Zone 4": row.zones["4"] || 0,
        "Zone 5": row.zones["5"] || 0,
        "Zone 6": row.zones["6"] || 0,
        "Zone 7": row.zones["7"] || 0,
        "Zone GD": row.zones["GD"] || 0,
        "GD Jours": row.gdDaysCount,
        "GD Montant (€)": Number(row.gdTotalAmount.toFixed(2)),
        "GD Heures Route": Number(row.gdTotalTravelHours.toFixed(2)),
        "CP (j)": leaves.cp,
        "Maladie (j)": leaves.maladie,
        "Sans Solde (j)": leaves.sans_solde,
        "Formation (j)": leaves.formation,
        "Autre Abs. (j)": leaves.autre,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Paie");

    const wscols = [
      { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
      { wch: 10 }, { wch: 14 }, { wch: 14 },
      { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
    ];
    worksheet['!cols'] = wscols;

    XLSX.writeFile(workbook, `Export_Paie_${dbPeriodStr}.xlsx`);
  }, [payrollData, startDate, endDate, dbPeriodStr]);

  // --- EXPORT PDF HISTORIQUE ---
  const handleExportHistoryPDF = useCallback(() => {
    if (!projectHistory) return;

    const doc = new jsPDF();

    // Couleurs par catégorie (4 catégories indépendantes)
    const colors = {
      etude: [59, 130, 246] as [number, number, number],       // Bleu - Bureau d'Étude
      suivi: [245, 158, 11] as [number, number, number],       // Orange - Suivi de chantier
      mes: [139, 92, 246] as [number, number, number],         // Violet - Mise en Service
      chantier: [16, 185, 129] as [number, number, number]     // Vert - Production
    };

    // Header
    doc.setFontSize(18);
    doc.setTextColor(5, 150, 105);
    doc.text(`HISTORIQUE - ${projectHistory.projectName}`, 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Code: ${projectHistory.projectCode}`, 14, 28);
    doc.text(`Période: ${projectHistory.firstEntryDate} à ${projectHistory.lastEntryDate}`, 14, 34);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 40);

    if (!projectHistory.isActive) {
      doc.setTextColor(220, 38, 38);
      doc.text('PROJET ARCHIVÉ', 14, 46);
    }

    // Tableau résumé global avec 4 catégories distinctes
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Résumé Global", 14, projectHistory.isActive ? 52 : 56);

    const totalBudget = projectHistory.budgetChantier + projectHistory.budgetBE;

    autoTable(doc, {
      startY: projectHistory.isActive ? 56 : 60,
      head: [['Type d\'activité', 'Heures', 'Budget', 'Progression']],
      body: [
        ['Bureau d\'Étude (BE)',
         `${projectHistory.totalHours.etude.toFixed(2)} h`,
         `${projectHistory.budgetBE} h`,
         projectHistory.budgetBE > 0 ? `${(projectHistory.totalHours.etude / projectHistory.budgetBE * 100).toFixed(1)}%` : '-'
        ],
        ['Suivi de Chantier',
         `${projectHistory.totalHours.suivi.toFixed(2)} h`,
         '-',
         '-'
        ],
        ['Mise en Service (MES)',
         `${projectHistory.totalHours.mise_en_service.toFixed(2)} h`,
         '-',
         '-'
        ],
        ['Production (Chantier)',
         `${projectHistory.totalHours.chantier.toFixed(2)} h`,
         `${projectHistory.budgetChantier} h`,
         projectHistory.budgetChantier > 0 ? `${projectHistory.progressChantier.toFixed(1)}%` : '-'
        ],
        ['TOTAL',
         `${projectHistory.totalHours.total.toFixed(2)} h`,
         totalBudget > 0 ? `${totalBudget} h` : '-',
         '-'
        ]
      ],
      theme: 'striped',
      headStyles: { fillColor: [5, 150, 105] },
      didParseCell: (data) => {
        // Appliquer les couleurs distinctes à la première colonne
        if (data.section === 'body' && data.column.index === 0) {
          if (data.row.index === 0) data.cell.styles.textColor = colors.etude;
          if (data.row.index === 1) data.cell.styles.textColor = colors.suivi;
          if (data.row.index === 2) data.cell.styles.textColor = colors.mes;
          if (data.row.index === 3) data.cell.styles.textColor = colors.chantier;
          if (data.row.index === 4) data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Tableau détail mensuel
    const finalY = (doc as unknown as JsPDFWithAutoTable).lastAutoTable.finalY + 15;
    doc.text("Détail Mensuel", 14, finalY);

    // Légende des couleurs
    const legendY = finalY + 4;
    doc.setFontSize(7);

    doc.setFillColor(...colors.etude);
    doc.rect(14, legendY, 6, 3, 'F');
    doc.setTextColor(0);
    doc.text('BE', 21, legendY + 2.5);

    doc.setFillColor(...colors.suivi);
    doc.rect(32, legendY, 6, 3, 'F');
    doc.text('Suivi', 39, legendY + 2.5);

    doc.setFillColor(...colors.mes);
    doc.rect(52, legendY, 6, 3, 'F');
    doc.text('MES', 59, legendY + 2.5);

    doc.setFillColor(...colors.chantier);
    doc.rect(72, legendY, 6, 3, 'F');
    doc.text('Production', 79, legendY + 2.5);

    const monthlyRows = projectHistory.monthlyData.map(m => [
      m.monthLabel,
      m.etude.toFixed(2),
      m.suivi.toFixed(2),
      m.mise_en_service.toFixed(2),
      m.chantier.toFixed(2),
      m.total.toFixed(2),
      m.cumulative.toFixed(2)
    ]);

    autoTable(doc, {
      startY: legendY + 6,
      head: [['Mois', 'BE', 'Suivi', 'MES', 'Production', 'Total', 'Cumul']],
      body: monthlyRows,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [71, 85, 105], textColor: 255 },
      columnStyles: {
        0: { fontStyle: 'bold' },
        5: { fontStyle: 'bold' },
        6: { fontStyle: 'bold', fillColor: [245, 245, 245] }
      },
      didParseCell: (data) => {
        // Colorer les en-têtes par catégorie
        if (data.section === 'head') {
          if (data.column.index === 1) data.cell.styles.fillColor = colors.etude;
          if (data.column.index === 2) data.cell.styles.fillColor = colors.suivi;
          if (data.column.index === 3) data.cell.styles.fillColor = colors.mes;
          if (data.column.index === 4) data.cell.styles.fillColor = colors.chantier;
        }
      },
      foot: [[
        'TOTAUX',
        projectHistory.totalHours.etude.toFixed(2),
        projectHistory.totalHours.suivi.toFixed(2),
        projectHistory.totalHours.mise_en_service.toFixed(2),
        projectHistory.totalHours.chantier.toFixed(2),
        projectHistory.totalHours.total.toFixed(2),
        '-'
      ]],
      footStyles: { fillColor: [226, 232, 240], fontStyle: 'bold', textColor: [30, 41, 59] }
    });

    doc.save(`historique_${projectHistory.projectCode}_${new Date().toISOString().split('T')[0]}.pdf`);
  }, [projectHistory]);

  // --- EXPORT PDF PAIE ---
  const downloadPayrollPDF = useCallback(async () => {
    // Charger les congés validés pour la période
    const { data: leavesData } = await supabase
      .from('leaves')
      .select('employee_id, type, start_date, end_date')
      .eq('status', 'validated')
      .lte('start_date', endDate)
      .gte('end_date', startDate);

    const countDaysPDF = (s: string, e: string): number => {
      const start2 = new Date(s);
      const end2 = new Date(e);
      const from = new Date(Math.max(start2.getTime(), new Date(startDate).getTime()));
      const to = new Date(Math.min(end2.getTime(), new Date(endDate).getTime()));
      let days = 0;
      const d = new Date(from);
      while (d <= to) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) days++;
        d.setDate(d.getDate() + 1);
      }
      return days;
    };

    const leavesByEmployee: Record<string, Record<string, number>> = {};
    for (const leave of leavesData || []) {
      if (!leavesByEmployee[leave.employee_id]) {
        leavesByEmployee[leave.employee_id] = { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
      }
      leavesByEmployee[leave.employee_id][leave.type] += countDaysPDF(leave.start_date, leave.end_date);
    }

    const doc = new jsPDF({ orientation: 'landscape' });

    // Titre
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Export Paie - ${filterMode === 'week' ? `Semaine ${selectedWeek}` : filterMode === 'month' ? `${MONTHS_FR[selectedMonth]} ${selectedYear}` : `Année ${selectedYear}`}`, 14, 15);

    // Sous-titre avec date de génération
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);
    doc.setTextColor(0);

    // Calcul des totaux pour le pied de tableau
    const totHours = parseFloat(payrollData.reduce((acc, r) => acc + r.totalHours, 0).toFixed(2));
    const totHS25 = parseFloat(payrollData.reduce((acc, r) => acc + r.overtime25, 0).toFixed(2));
    const totHS50 = parseFloat(payrollData.reduce((acc, r) => acc + r.overtime50, 0).toFixed(2));
    const totSunday = parseFloat(payrollData.reduce((acc, r) => acc + r.sundayHours, 0).toFixed(2));
    const totPaniers = payrollData.reduce((acc, r) => acc + r.totalMeals, 0);
    const totTrajet = payrollData.reduce((acc, r) => acc + r.totalTrajet, 0);
    const totTransport = payrollData.reduce((acc, r) => acc + r.totalTransport, 0);
    const totGDDays = payrollData.reduce((acc, r) => acc + r.gdDaysCount, 0);
    const totGDAmount = parseFloat(payrollData.reduce((acc, r) => acc + r.gdTotalAmount, 0).toFixed(2));
    const totGDRoute = parseFloat(payrollData.reduce((acc, r) => acc + r.gdTotalTravelHours, 0).toFixed(2));
    const totZ1A = payrollData.reduce((acc, r) => acc + (r.zones['1A'] || 0), 0);
    const totZ1B = payrollData.reduce((acc, r) => acc + (r.zones['1B'] || 0), 0);
    const totZ2 = payrollData.reduce((acc, r) => acc + (r.zones['2'] || 0), 0);
    const totZ3 = payrollData.reduce((acc, r) => acc + (r.zones['3'] || 0), 0);
    const totZ4 = payrollData.reduce((acc, r) => acc + (r.zones['4'] || 0), 0);
    const totZ5 = payrollData.reduce((acc, r) => acc + (r.zones['5'] || 0), 0);
    const totZ6 = payrollData.reduce((acc, r) => acc + (r.zones['6'] || 0), 0);
    const totZ7 = payrollData.reduce((acc, r) => acc + (r.zones['7'] || 0), 0);
    const totCP = payrollData.reduce((acc, r) => acc + ((leavesByEmployee[r.employeeId]?.cp) || 0), 0);
    const totMaladie = payrollData.reduce((acc, r) => acc + ((leavesByEmployee[r.employeeId]?.maladie) || 0), 0);
    const totSansSolde = payrollData.reduce((acc, r) => acc + ((leavesByEmployee[r.employeeId]?.sans_solde) || 0), 0);
    const totFormation = payrollData.reduce((acc, r) => acc + ((leavesByEmployee[r.employeeId]?.formation) || 0), 0);
    const totAutre = payrollData.reduce((acc, r) => acc + ((leavesByEmployee[r.employeeId]?.autre) || 0), 0);

    // Tableau des données avec détail des zones et congés
    const tableColumn = [
      "Employé", "Total H.", "dont HS 25%", "dont HS 50%", "dont H. Dim.",
      "Trajets", "Transp.", "Paniers",
      "Z1A", "Z1B", "Z2", "Z3", "Z4", "Z5", "Z6", "Z7", "GD",
      "GD €", "GD Rte",
      "CP", "Mal.", "SS", "Form.", "Autre"
    ];

    const tableRows = payrollData.map(row => {
      const leaves = leavesByEmployee[row.employeeId] || { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
      return [
        row.employeeName,
        row.totalHours.toFixed(2),
        row.overtime25 > 0 ? row.overtime25.toFixed(2) : '-',
        row.overtime50 > 0 ? row.overtime50.toFixed(2) : '-',
        row.sundayHours > 0 ? row.sundayHours.toFixed(2) : '-',
        row.totalTrajet,
        row.totalTransport,
        row.totalMeals,
        row.zones['1A'] || '-',
        row.zones['1B'] || '-',
        row.zones['2'] || '-',
        row.zones['3'] || '-',
        row.zones['4'] || '-',
        row.zones['5'] || '-',
        row.zones['6'] || '-',
        row.zones['7'] || '-',
        row.gdDaysCount > 0 ? row.gdDaysCount : '-',
        row.gdTotalAmount > 0 ? row.gdTotalAmount.toFixed(2) : '-',
        row.gdTotalTravelHours > 0 ? row.gdTotalTravelHours.toFixed(2) : '-',
        leaves.cp > 0 ? leaves.cp : '-',
        leaves.maladie > 0 ? leaves.maladie : '-',
        leaves.sans_solde > 0 ? leaves.sans_solde : '-',
        leaves.formation > 0 ? leaves.formation : '-',
        leaves.autre > 0 ? leaves.autre : '-'
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      foot: [[
        'TOTAUX',
        totHours.toFixed(2),
        totHS25 > 0 ? totHS25.toFixed(2) : '-',
        totHS50 > 0 ? totHS50.toFixed(2) : '-',
        totSunday > 0 ? totSunday.toFixed(2) : '-',
        totTrajet,
        totTransport,
        totPaniers,
        totZ1A || '-',
        totZ1B || '-',
        totZ2 || '-',
        totZ3 || '-',
        totZ4 || '-',
        totZ5 || '-',
        totZ6 || '-',
        totZ7 || '-',
        totGDDays > 0 ? totGDDays : '-',
        totGDAmount > 0 ? totGDAmount.toFixed(2) : '-',
        totGDRoute > 0 ? totGDRoute.toFixed(2) : '-',
        totCP > 0 ? totCP : '-',
        totMaladie > 0 ? totMaladie : '-',
        totSansSolde > 0 ? totSansSolde : '-',
        totFormation > 0 ? totFormation : '-',
        totAutre > 0 ? totAutre : '-'
      ]],
      startY: 28,
      theme: 'grid',
      styles: { fontSize: 7, halign: 'center', cellPadding: 2 },
      headStyles: { fillColor: [0, 133, 66], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold', cellWidth: 30 },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' }
      }
    });

    doc.save(`Export_Paie_${dbPeriodStr}.pdf`);
  }, [payrollData, startDate, endDate, dbPeriodStr, filterMode, selectedWeek, selectedMonth, selectedYear]);

  return { handleExportPDFAnalytics, downloadXLSX, downloadPayrollPDF, handleExportHistoryPDF };
}
